import React, { useCallback, useEffect, useState } from "react";
import { mergeRight } from "ramda";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DialogContent from "@mui/material/DialogContent";
import { lensProp, set } from "ramda";
import Grid from "@mui/material/Grid";
import { FetchError } from "../util/fetchUtils";
import { useDispatch } from "react-redux";
import { logOut } from "../auth/authActions";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { notBlank } from "../util/ext";
import {
  useContentProviderCreate,
  useContentProviderUpdate,
} from "./contentProviderServices";
import TextField from "@mui/material/TextField";
import OrganizationAutoComplete from "../user-admin/OrganizationAutoComplete";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const initialState = {
  fields: {
    name: "",
  },
};

const ContentProviderDialog = ({ open, contentProvider, onClose, onSave }) => {
  const dispatch = useDispatch();
  const [fields, setFields] = useState(initialState.fields);
  const [errorAlert, setErrorAlert] = useState({ open: false });

  useEffect(() => {
    if (notBlank(contentProvider)) {
      setFields(contentProvider);
    }
  }, [contentProvider]);

  const onFieldChanged = field => value => {
    const val = value.target ? value.target.value : value;
    const update = set(lensProp(field), val, fields);
    setFields(update);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const handleError = useCallback(
    (message, err = {}) => {
      if (err instanceof FetchError) {
        if (err.status === 401) {
          dispatch(logOut());
        } else {
          showAlert(message);
        }
      } else {
        if (notBlank(err)) {
          showAlert(err.message);
        } else {
          showAlert(message);
        }
      }
    },
    [dispatch],
  );

  const contentProviderCreate = useContentProviderCreate();
  const contentProviderUpdate = useContentProviderUpdate();
  const handleOnSave = useCallback(async () => {
    if (notBlank(fields.name)) {
      try {
        if (notBlank(fields.id)) {
          await contentProviderUpdate.mutateAsync({
            resourceIds: {
              contentProviderId: contentProvider.id,
            },
            data: fields,
          });
        } else {
          await contentProviderCreate.mutateAsync({
            data: fields,
          });
        }
        onSave(fields);
      } catch (err) {
        handleError("Unable to save Content Provider.", err);
      }
    } else {
      handleError("Content Provider Name is required.");
    }
  }, [
    handleError,
    onSave,
    contentProviderCreate,
    contentProviderUpdate,
    contentProvider,
    fields,
  ]);

  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">New Content Provider</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter a name for the new Content Provider.
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              id="name"
              label="Name"
              fullWidth
              value={fields.name || ""}
              onChange={onFieldChanged("name")}
            />
          </Grid>

          <Grid item xs={12}>
            <OrganizationAutoComplete
              value={fields.organizationId}
              filterFunction={org => org.active}
              onChange={onFieldChanged(["organizationId"])}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleOnSave} color="primary">
          Save
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ContentProviderDialog;
